import type { ReadOnlyShortcutRow } from "~/keyboard/shortcutSettingsModel";

import { Box } from "@mui/material";
import { Fragment } from "react";

import Keycap from "~/components/keyboard/Keycap";
import SettingRow from "~/components/settings/SettingRow";
import SettingsSection from "~/components/settings/SettingsSection";
import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import { GRID_BUILTIN_SHORTCUTS } from "~/keyboard/keyboardHelpModel";

interface BuiltInShortcutsSectionProps {
	appShortcuts: ReadOnlyShortcutRow[];
	isMac: boolean;
}

interface BuiltInEntryProps {
	bindings: string[];
	isMac: boolean;
	label: string;
}

const BuiltInEntry = ({ bindings, isMac, label }: BuiltInEntryProps) => (
	<SettingRow
		control={
			<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
				{bindings.map((binding, index) => (
					<Fragment key={binding}>
						{index > 0 && <CaptionText color="text.disabled">or</CaptionText>}
						<Keycap binding={binding} isMac={isMac} />
					</Fragment>
				))}
			</Box>
		}
		label={label}
	/>
);

const BuiltInShortcutsSection = ({ appShortcuts, isMac }: BuiltInShortcutsSectionProps) => (
	<SettingsSection
		description="These shortcuts are built into the grid, browser, and core app flows and can't be changed."
		title="Built-in (grid/browser)"
	>
		{appShortcuts.length > 0 && (
			<Box sx={{ mb: 1.5 }}>
				<BodyTextSmall bold color="text.secondary">
					App
				</BodyTextSmall>
			</Box>
		)}
		{appShortcuts.map((entry) => (
			<BuiltInEntry bindings={entry.bindings} isMac={isMac} key={entry.id} label={entry.label} />
		))}
		<Box sx={{ mb: 1.5, mt: 2.5 }}>
			<BodyTextSmall bold color="text.secondary">
				Grid
			</BodyTextSmall>
		</Box>
		{GRID_BUILTIN_SHORTCUTS.map((entry) => (
			<BuiltInEntry bindings={entry.bindings} isMac={isMac} key={entry.id} label={entry.label} />
		))}
	</SettingsSection>
);

export default BuiltInShortcutsSection;
